import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mantine/dates';
import CheckIcon from '@mui/icons-material/CheckRounded';
import { Menu } from '@mui/material';

import calenderIcon from '../../../images/icons/calendar.svg';
import { dayjs } from '../../../utils/dayjs-utils';
import { getDateNDaysAgo } from '../../../utils/global';
import ButtonComponent from '../ButtonComponent/ButtonComponent';

import './custom-compound-date-picker.scss';

import ChevronUp from '../../Icons/icons/ChevronUp';

const datepickerOptions = [
  { id: '1', label: 'Last 7d', value: 7 },
  { id: '2', label: 'Last 14d', value: 14 },
  { id: '3', label: 'Last 30d', value: 30 },
  { id: '4', label: 'Last 3mo', value: 90 },
  { id: '5', label: 'All Time', value: -1 },
  { id: '6', label: 'Custom Range', value: 0 },
];

export default function CustomCompoundDatePicker(props) {
  const {
    selectedDays,
    showDatePicker,
    setShowDatePicker,
    dateRange,
    customCalenderIcon,
    containerStyle,
    anchorElem,
    className,
    buttonWrapperClassName='',
  } = props;
  const formatSelectedDate = () => {
    if (selectedDays === 0) {
      return `${dayjs(dateRange[0]).format('DD MMM YYYY')} - ${dayjs(dateRange[1]).format('DD MMM YYYY')}`;
    }
    return datepickerOptions?.find((option) => option.value === selectedDays)?.label;
  };

  return (
    <div className="dateRangeDiv">
      <div onClick={(e) => setShowDatePicker(e.currentTarget)}  className={buttonWrapperClassName} >
        <button style={containerStyle} className={`date-select-button-container ${className}`} type="button">
          {customCalenderIcon ? customCalenderIcon : <img src={calenderIcon} alt="" />}
          <span>{selectedDays === undefined ? 'Select Date' : formatSelectedDate()}</span>
          <ChevronUp
            width={25}
            height={25}
            color="currentColor"
            style={{
              transform: showDatePicker ? 'rotate(0deg)' : 'rotate(-180deg)',
              transition: 'transform 200ms ease-in-out',
              marginLeft: '-5px',
              marginRight: '-10px',
            }}
          />
        </button>
      </div>

      <Menu
        anchorEl={anchorElem ? anchorElem : showDatePicker}
        open={Boolean(showDatePicker)}
        onClose={() => setShowDatePicker(null)}
        PaperProps={{
          sx: {
            width: '465px',
            zIndex: 9999,
            overflow: 'visible',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <DatePickerPopupContent {...props} />
      </Menu>
    </div>
  );
}

export function DatePickerPopupContent({
  selectedDays,
  setSelectedDays,
  setShowDatePicker,
  dateRange,
  setDateRange,
  maxDate,
  allowSingleDateInRage,
  isolated,
  reversed,
}) {
  const [tempSelectedDays, setTempSelectedDays] = useState(selectedDays);
  const [tempDateRange, setTempDateRange] = useState(dateRange);

  useEffect(() => {
    if (isolated) {
      setSelectedDays(tempSelectedDays);
      handleSelectDateRange(tempSelectedDays);
    }
  }, [isolated, tempSelectedDays, tempDateRange]);

  const handleSelectDateRange = (days) => {
    if (days === -1) {
      setDateRange([null, null]);
    } else if (days === 0) {
      if (tempDateRange[0] && tempDateRange[1]) {
        tempDateRange[0] = new Date(dayjs(tempDateRange[0]).utc(true));
        tempDateRange[1] = new Date(dayjs(tempDateRange[1]).utc(true));
      }
      setDateRange(tempDateRange);
    } else {
      const from = getDateNDaysAgo(days);
      const to = new Date();
      setDateRange([from, to]);
    }
  };

  const handleApply = (days) => {
    handleSelectDateRange(days);
    setSelectedDays(days);
    if (setShowDatePicker) {
      setShowDatePicker(false);
    }
  };

  const handleCancel = () => {
    setSelectedDays(selectedDays);
    setDateRange(dateRange);
    setTempSelectedDays(selectedDays);
    setTempDateRange(selectedDays === 0 ? dateRange : []);
    setShowDatePicker(false);
  };

  return (
    <div className={`date-picker-dropdown ${reversed ? 'reversed' : ''}`}>
      <div className="left-container" style={{ borderRight: '1px solid var(--light-grey)' }}>
        {datepickerOptions?.map((option) => (
          <p
            key={option.id}
            className="date-picker-option"
            onClick={() => {
              if (option.value !== 0) {
                let newDateRange = [];
                handleApply(option.value);
                setTempSelectedDays(option.value);
                if (option.value === -1) newDateRange = [null, null];
                else newDateRange = [dayjs().subtract(option.value, 'days').toDate(), dayjs().toDate()];
                setTempDateRange(newDateRange);
              } else {
                setTempSelectedDays(option.value);
              }
            }}
            style={{
              color: tempSelectedDays === option?.value ? 'var(--purple-p-400)' : 'var(--black)',
              backgroundColor: tempSelectedDays === option?.value && 'var(--purple-p-10)',
            }}>
            {option.label}
            {tempSelectedDays === option.value && <CheckIcon color="var(--saral-purple)" sx={{ fontSize: '16px' }} />}
          </p>
        ))}
      </div>
      <div className="right-container">
        <div className="datepicker-calendar-div">
          <DatePicker
            type="range"
            maxDate={maxDate}
            allowSingleDateInRange={allowSingleDateInRage}
            value={tempDateRange}
            onChange={(val) => {
              setTempDateRange(val);
              setTempSelectedDays(0);
            }}
          />
        </div>
        {(tempDateRange[0] || tempDateRange[1]) && (
          <div className="date-display-container">
            <div className="date-display">
              {tempDateRange[0] ? dayjs(tempDateRange[0]).format('DD/MM/YYYY') : 'N/A'}
            </div>
            <span>-</span>
            <div className="date-display">
              {tempDateRange[1] ? dayjs(tempDateRange[1]).format('DD/MM/YYYY') : 'N/A'}
            </div>
          </div>
        )}
        {isolated && <br />}
        {!isolated && (
          <div className="custom-compound-datepicker-bottom-button-container">
            <ButtonComponent variant="secondary" style={{ height: '39px', width: '142px' }} onClick={handleCancel}>
              Cancel
            </ButtonComponent>
            <ButtonComponent
              style={{ height: '39px', width: '142px', border: '1px solid var(--border-highlight)' }}
              onClick={() => handleApply(0)}
              disabled={!tempDateRange[0] || !tempDateRange[1]}>
              Apply
            </ButtonComponent>
          </div>
        )}
      </div>
    </div>
  );
}
